import type React from "react"
import { allowsMotion } from "../utils"

// =============================================================================
// Helpers
// =============================================================================

export type ScrollDirection = "start" | "end"

function getScrollPadding(scroller: HTMLElement) {
  const { scrollPaddingLeft, scrollPaddingRight } = getComputedStyle(scroller)
  return {
    left: parseFloat(scrollPaddingLeft) || 0,
    right: parseFloat(scrollPaddingRight) || 0
  }
}

// Scrolls only the scroller, unlike `scrollIntoView`, which would also scroll
// the page to the scroller on load
export function scrollItemClearOfFades(scroller: HTMLElement, item: Element) {
  const scrollPadding = getScrollPadding(scroller)
  const scrollerRect = scroller.getBoundingClientRect()
  const itemRect = item.getBoundingClientRect()
  const hiddenLeft = scrollerRect.left + scrollPadding.left - itemRect.left
  const hiddenRight =
    itemRect.right - (scrollerRect.right - scrollPadding.right)

  if (hiddenLeft > 0) scroller.scrollLeft -= hiddenLeft
  else if (hiddenRight > 0) scroller.scrollLeft += hiddenRight
}

// Scrolls by the width between the fades, so the item cut off by one fade
// becomes the first fully visible item. Narrow containers, where the fades
// leave little room, still scroll by half their width.
export function scrollByPage(
  scroller: HTMLElement,
  direction: ScrollDirection
) {
  const scrollPadding = getScrollPadding(scroller)
  const widthBetweenFades =
    scroller.clientWidth - scrollPadding.left - scrollPadding.right
  scroller.scrollBy({
    left:
      (direction === "start" ? -1 : 1) *
      Math.max(widthBetweenFades, scroller.clientWidth / 2),
    behavior: allowsMotion() ? "smooth" : "auto"
  })
}

// The browser's own scroll on focus doesn't reliably honor `scroll-padding`,
// so the focused item is scrolled into view again explicitly.
export function scrollFocusedItemIntoView(
  event: React.FocusEvent<HTMLElement>
) {
  if (event.target === event.currentTarget) return
  if (!event.target.matches(":focus-visible")) return
  event.target.scrollIntoView({ block: "nearest", inline: "nearest" })
}
